import React, { useState, useEffect } from 'react';
import { StraightPiece, Position } from '../types/game';

interface StraightChessBoardProps {
  board: (StraightPiece | null)[][];
  selectedPiece?: Position;
  possibleMoves?: Position[];
  lastMill?: Position[];
  onSelectPosition: (row: number, col: number) => void;
  isAIThinking?: boolean;
}

const StraightChessBoard: React.FC<StraightChessBoardProps> = ({
  board,
  selectedPiece,
  possibleMoves = [],
  lastMill = [],
  onSelectPosition,
  isAIThinking = false
}) => {
  // 响应式状态管理
  const [windowSize, setWindowSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return {
        width: window.innerWidth,
        height: window.innerHeight
      };
    }
    return { width: 1024, height: 768 };
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // 直棋的24个有效位置（3个同心正方形 + 4条对角线）
  const validPositions: Position[] = [
    // 外层正方形 (8个点)
    { row: 0, col: 0 }, { row: 0, col: 3 }, { row: 0, col: 6 },
    { row: 3, col: 0 },                           { row: 3, col: 6 },
    { row: 6, col: 0 }, { row: 6, col: 3 }, { row: 6, col: 6 },

    // 中层正方形 (8个点)
    { row: 1, col: 1 }, { row: 1, col: 3 }, { row: 1, col: 5 },
    { row: 3, col: 1 },                           { row: 3, col: 5 },
    { row: 5, col: 1 }, { row: 5, col: 3 }, { row: 5, col: 5 },

    // 内层正方形 (8个点)
    { row: 2, col: 2 }, { row: 2, col: 3 }, { row: 2, col: 4 },
    { row: 3, col: 2 },                           { row: 3, col: 4 },
    { row: 4, col: 2 }, { row: 4, col: 3 }, { row: 4, col: 4 },
  ];

  // 检查是否为有效位置
  const isValidPosition = (row: number, col: number): boolean => {
    return validPositions.some(pos => pos.row === row && pos.col === col);
  };

  // 检查位置是否在最后形成的成直中
  const isInLastMill = (row: number, col: number): boolean => {
    return lastMill.some(pos => pos.row === row && pos.col === col);
  };

  // 检查是否为可能的移动位置
  const isPossibleMove = (row: number, col: number): boolean => {
    return possibleMoves.some(pos => pos.row === row && pos.col === col);
  };

  // 检查是否为选中的棋子
  const isSelectedPiece = (row: number, col: number): boolean => {
    return selectedPiece?.row === row && selectedPiece?.col === col;
  };

  // 获取连接线（用于绘制棋盘线条）
  const getConnections = () => {
    return [
      // 外层正方形
      { from: { row: 0, col: 0 }, to: { row: 0, col: 6 } },
      { from: { row: 0, col: 6 }, to: { row: 6, col: 6 } },
      { from: { row: 6, col: 6 }, to: { row: 6, col: 0 } },
      { from: { row: 6, col: 0 }, to: { row: 0, col: 0 } },

      // 中层正方形
      { from: { row: 1, col: 1 }, to: { row: 1, col: 5 } },
      { from: { row: 1, col: 5 }, to: { row: 5, col: 5 } },
      { from: { row: 5, col: 5 }, to: { row: 5, col: 1 } },
      { from: { row: 5, col: 1 }, to: { row: 1, col: 1 } },

      // 内层正方形
      { from: { row: 2, col: 2 }, to: { row: 2, col: 4 } },
      { from: { row: 2, col: 4 }, to: { row: 4, col: 4 } },
      { from: { row: 4, col: 4 }, to: { row: 4, col: 2 } },
      { from: { row: 4, col: 2 }, to: { row: 2, col: 2 } },

      { from: { row: 3, col: 0 }, to: { row: 3, col: 2 } },
      { from: { row: 3, col: 4 }, to: { row: 3, col: 6 } },
      { from: { row: 0, col: 3 }, to: { row: 2, col: 3 } },
      { from: { row: 4, col: 3 }, to: { row: 6, col: 3 } },

      // 对角线连接 - 完整版本
      { from: { row: 0, col: 0 }, to: { row: 2, col: 2 } },
      { from: { row: 0, col: 6 }, to: { row: 2, col: 4 } },
      { from: { row: 6, col: 0 }, to: { row: 4, col: 2 } },
      { from: { row: 6, col: 6 }, to: { row: 4, col: 4 } },

      // 内层正方形的对角线
      

      // 中层正方形的对角线
      
    ];
  };

  // 计算棋盘坐标转换（响应式设计）
  const getBoardCoordinates = (row: number, col: number) => {
    const cellSize = windowSize.width < 640 ? 40 :  // 移动端
                    windowSize.width < 1024 ? 50 : // 平板
                    90;                              // 桌面端（增大）
    const padding = windowSize.width < 640 ? 30 :   // 移动端
                    windowSize.width < 1024 ? 35 :  // 平板
                    60;                              // 桌面端（增大）

    return {
      x: padding + col * cellSize,
      y: padding + row * cellSize
    };
  };

  // 计算SVG尺寸（响应式）
  const getSvgSize = () => {
    if (windowSize.width < 640) {
      return { width: 300, height: 300 }; // 移动端
    } else if (windowSize.width < 1024) {
      return { width: 370, height: 370 }; // 平板
    }
    return { width: 660, height: 660 };     // 桌面端（增大）
  };

  const svgSize = getSvgSize();

  return (
    <div className="flex items-center justify-center w-full h-full">
      <div className="relative bg-amber-100 rounded-xl shadow-2xl border-4 border-amber-600 p-2 w-full max-w-[700px] aspect-square">
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${svgSize.width} ${svgSize.height}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ display: 'block' }}
        >
          {/* 绘制连接线 */}
          {getConnections().map((connection, index) => {
            const from = getBoardCoordinates(connection.from.row, connection.from.col);
            const to = getBoardCoordinates(connection.to.row, connection.to.col);
            const lineWidth = windowSize.width < 640 ? 2 :
                             windowSize.width < 1024 ? 2.5 : 4; // 桌面端线条加粗

            return (
              <line
                key={index}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke="#654321"
                strokeWidth={lineWidth}
                strokeLinecap="round"
              />
            );
          })}

          {/* 绘制棋盘点位 */}
          {validPositions.map((position, index) => {
            const coords = getBoardCoordinates(position.row, position.col);
            const piece = board[position.row]?.[position.col];
            const selected = isSelectedPiece(position.row, position.col);
            const possibleMove = isPossibleMove(position.row, position.col);
            const inLastMill = isInLastMill(position.row, position.col);
            const valid = isValidPosition(position.row, position.col);

            const baseRadius = windowSize.width < 640 ? 14 :
                             windowSize.width < 1024 ? 20 : 32; // 桌面端增大
            const pieceRadius = baseRadius - 5; // 增大棋子半径

            return (
              <g key={index}>
                {/* 底座圆圈 */}
                <circle
                  cx={coords.x}
                  cy={coords.y}
                  r={baseRadius}
                  fill={selected ? "#FCD34D" : (possibleMove ? "#BBF7D0" : "#FEF3C7")}
                  stroke={selected ? "#F59E0B" : (possibleMove ? "#10B981" : "#92400E")}
                  strokeWidth={selected ? 3 : (possibleMove ? 3 : 2)}
                  className={valid && !piece && !isAIThinking ? "cursor-pointer hover:fill-amber-200" : ""}
                  onClick={() => {
                    if (valid && !isAIThinking) {
                      onSelectPosition(position.row, position.col);
                    }
                  }}
                />

                {/* 成直高亮 */}
                {inLastMill && (
                  <circle
                    cx={coords.x}
                    cy={coords.y}
                    r={baseRadius + 4}
                    fill="none"
                    stroke="#EF4444"
                    strokeWidth="2"
                    strokeDasharray="6,4"
                  />
                )}

                {/* 棋子 */}
                {piece && (
                  <g
                    className={`cursor-pointer ${valid && !isAIThinking ? "" : ""}`}
                    onClick={() => {
                      if (valid && !isAIThinking) {
                        onSelectPosition(position.row, position.col);
                      }
                    }}
                  >
                    {/* 棋子阴影 */}
                    <circle
                      cx={coords.x + 2}
                      cy={coords.y + 2}
                      r={pieceRadius}
                      fill="#000000"
                      opacity="0.3"
                    />
                    {/* 棋子主体 */}
                    <circle
                      cx={coords.x}
                      cy={coords.y}
                      r={pieceRadius}
                      fill={piece.player === 'black' ? '#1F2937' : '#FFFFFF'}
                      stroke={piece.player === 'black' ? '#111827' : '#9CA3AF'}
                      strokeWidth="2"
                    />
                    {/* 棋子高光 */}
                    <circle
                      cx={coords.x - pieceRadius * 0.3}
                      cy={coords.y - pieceRadius * 0.3}
                      r={pieceRadius * 0.3}
                      fill="#FFFFFF"
                      opacity={piece.player === 'black' ? 0.4 : 0.8}
                    />
                    {/* 白棋内圈装饰 */}
                    {piece.player === 'white' && (
                      <circle
                        cx={coords.x}
                        cy={coords.y}
                        r={pieceRadius - 6}
                        fill="none"
                        stroke="#D1D5DB"
                        strokeWidth="1"
                      />
                    )}
                  </g>
                )}

                {/* 可能移动位置的指示器 */}
                {possibleMove && !piece && (
                  <circle
                    cx={coords.x}
                    cy={coords.y}
                    r={windowSize.width < 640 ? 2 : 4} // 桌面端指示器增大
                    fill="#10B981"
                  />
                )}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

export default StraightChessBoard;